import { api } from '../lib/api';
import { Loading, Panel, useAsync } from './ui';

/**
 * Per-device security posture.
 *
 * The reason this component exists rather than a row of green ticks: the two
 * factory-reset controls have genuinely different answers, and merging them —
 * which any "restrictions applied: 9/9" summary would do — tells an
 * administrator the device is protected against a bypass it is wide open to.
 * Every control therefore renders its own status word, and an uncontrollable
 * one is styled as a limitation rather than a failure, because there is no
 * setting anyone can change to fix it.
 */

type ControlStatus =
  | 'supported'
  | 'partially_supported'
  | 'unsupported_android_version'
  | 'requires_device_owner'
  | 'oem_dependent'
  | 'google_enterprise_dependent'
  | 'not_controllable_by_dpc'
  | 'unknown';

type ControlState = 'enforced' | 'available_not_enabled' | 'not_available';

interface SecurityControl {
  key: string;
  label: string;
  status: ControlStatus;
  state: ControlState;
  detail: string;
  androidMechanism?: string;
  minApiLevel?: number;
}

interface SecurityResponse {
  controls: SecurityControl[];
  summary: { enforced: number; available: number; blocked: number };
  oemGuidance: string;
}

interface HealthSignal {
  key: string;
  label: string;
  grade: 'healthy' | 'warning' | 'critical';
  detail: string;
}

interface HealthResponse {
  grade: 'healthy' | 'warning' | 'critical';
  signals: HealthSignal[];
  securityState: string;
  securityStateDetail: { label: string; detail: string; severity: string };
  enrollmentCount: number;
}

/** Short word shown against each control. Never "blocked" for a Settings-only win. */
const STATUS_LABEL: Record<ControlStatus, string> = {
  supported: 'SUPPORTED',
  partially_supported: 'PARTIAL',
  unsupported_android_version: 'UNSUPPORTED',
  requires_device_owner: 'NO DEVICE OWNER',
  oem_dependent: 'OEM DEPENDENT',
  google_enterprise_dependent: 'GOOGLE DEPENDENT',
  not_controllable_by_dpc: 'NOT CONTROLLABLE',
  unknown: 'UNKNOWN',
};

const STATE_LABEL: Record<ControlState, string> = {
  enforced: 'ENFORCED',
  available_not_enabled: 'NOT ENABLED',
  not_available: '—',
};

export function DeviceSecurityPanel({ deviceId }: { deviceId: string }) {
  const security = useAsync(
    (signal) => api.get<SecurityResponse>(`/devices/${deviceId}/security`, signal),
    [deviceId],
  );
  const health = useAsync(
    (signal) => api.get<HealthResponse>(`/devices/${deviceId}/health`, signal),
    [deviceId],
  );

  return (
    <>
      <Panel title="Device health">
        {health.loading && <Loading what="health" />}
        {health.data && (
          <>
            <div className={`health-grade health-${health.data.grade}`}>
              {health.data.grade.toUpperCase()}
            </div>
            <p className="small muted">{health.data.securityStateDetail.detail}</p>
            <dl className="security-controls">
              {health.data.signals.map((s) => (
                <div key={s.key} className="security-row">
                  <dt>{s.label}</dt>
                  <dd>
                    <span className="badge" data-grade={s.grade}>
                      {s.grade.toUpperCase()}
                    </span>
                    <div className="small muted">{s.detail}</div>
                  </dd>
                </div>
              ))}
            </dl>
            {health.data.enrollmentCount > 1 && (
              <p className="small">
                This hardware has enrolled <strong>{health.data.enrollmentCount}</strong> times.
                Re-enrolment means the agent&rsquo;s local storage was destroyed.
              </p>
            )}
          </>
        )}
      </Panel>

      <Panel title="Security controls — what is actually protecting this device">
        {security.loading && <Loading what="security posture" />}
        {security.data && (
          <>
            <dl className="security-controls">
              {security.data.controls.map((c) => (
                <div
                  key={c.key}
                  className="security-row"
                  data-limitation={c.status === 'not_controllable_by_dpc' ? 'true' : undefined}
                >
                  <dt>{c.label}</dt>
                  <dd>
                    <span className="badge" data-status={c.status}>
                      {STATUS_LABEL[c.status]}
                    </span>
                    {c.state !== 'not_available' && (
                      <span className="badge" data-state={c.state}>
                        {STATE_LABEL[c.state]}
                      </span>
                    )}
                    <div className="small muted">{c.detail}</div>
                    {c.androidMechanism && (
                      <div className="small muted mono">
                        {c.androidMechanism}
                        {c.minApiLevel ? ` · API ${c.minApiLevel}+` : ''}
                      </div>
                    )}
                  </dd>
                </div>
              ))}
            </dl>

            <p className="small muted">
              {security.data.summary.enforced} enforced ·{' '}
              {security.data.summary.available} available but not enabled ·{' '}
              {security.data.summary.blocked} unavailable on this device.
            </p>
          </>
        )}
      </Panel>
    </>
  );
}
